/**
 * How old a position fix already is, in milliseconds.
 *
 * Its own module, with no imports, so it can be driven by a plain node test —
 * `location.ts` pulls `expo-location` and cannot be. Small, but it is the one
 * piece of arithmetic that decides whether the server can tell a live fix from
 * a five-minute-old cached one, so it is worth being able to test directly.
 *
 * Computed as a DELTA on the device rather than sent as a timestamp. Both
 * readings come from the same clock, so a phone whose clock is wrong by an hour
 * still reports a correct age; an absolute timestamp from that phone would be
 * wrong by an hour and would either look permanently stale (clock slow) or
 * arrive from the future (clock fast).
 */

/**
 * Age of a fix taken at `timestampMs`, as of `now`.
 *
 * Never negative. A fix timestamped in the future is a clock artefact, not a
 * prophecy, and reporting a negative age would ask the server to date a
 * coordinate ahead of its own clock — where no staleness rule could expire it.
 * Zero is the honest floor: "as fresh as anything can be".
 *
 * A missing or non-finite timestamp also yields 0. Some platforms have been
 * known not to populate it, and the pre-existing behaviour — treat the fix as
 * current — is the right fallback: dating an unknown fix as old would make its
 * owner undiscoverable rather than merely optimistic.
 */
export function fixAgeMs(
  timestampMs: number | null | undefined,
  now: number = Date.now(),
): number {
  if (timestampMs === null || timestampMs === undefined) return 0;
  if (!Number.isFinite(timestampMs)) return 0;
  return Math.max(0, now - timestampMs);
}

/**
 * Whether the OS's cached position is recent enough to use INSTEAD of paying
 * for a live read.
 *
 * This is the battery decision. Reading the cached fix costs nothing — the OS
 * already holds it — while `getCurrentPositionAsync` spins up the location
 * stack and is budgeted at 4–12 seconds. A heartbeat that forces a live read
 * every time pays that on every beat, forever, mostly to re-learn that someone
 * has not moved from their table.
 *
 * It is only safe to prefer the cache because the age now travels to the server
 * with the fix. Before that, using a cached position was a silent lie: the row
 * was stamped as if measured on arrival, so a stale coordinate was
 * indistinguishable from a live one. Now it is declared, and every consumer can
 * see exactly how old the answer is.
 *
 * `acceptWithinMs <= 0` means the caller wants the freshest possible answer and
 * disables this entirely — the behaviour of every path that is not the
 * heartbeat, because a refresh a person asked for should not be served from
 * cache.
 *
 * A missing timestamp reads as age 0 (see `fixAgeMs`), which would accept the
 * cache. That is deliberate and consistent: an undated fix is treated as
 * current everywhere rather than being second-guessed in one place.
 */
export function cachedFixIsFreshEnough(
  timestampMs: number | null | undefined,
  acceptWithinMs: number,
  now: number = Date.now(),
): boolean {
  if (!Number.isFinite(acceptWithinMs) || acceptWithinMs <= 0) return false;
  return fixAgeMs(timestampMs, now) <= acceptWithinMs;
}
